import React, { useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import './QuizModal.css';

const QuizModal = ({ open, slug, accessToken, questions, onSubmitted }) => {
  const orderedQuestions = useMemo(
    () => [...(questions || [])].sort((a, b) => (a.order || 0) - (b.order || 0)),
    [questions]
  );
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  if (!open) return null;

  const setAnswer = (questionId, patch) => {
    setAnswers((prev) => ({
      ...prev,
      [questionId]: { ...(prev[questionId] || {}), ...patch },
    }));
  };

  const toggleOption = (question, optionId) => {
    const current = answers[question.id] || { option_ids: [], text_answer: '' };
    if (question.question_type === 'single') {
      setAnswer(question.id, { option_ids: [optionId] });
      return;
    }
    const selected = new Set(current.option_ids || []);
    if (selected.has(optionId)) {
      selected.delete(optionId);
    } else {
      selected.add(optionId);
    }
    setAnswer(question.id, { option_ids: Array.from(selected) });
  };

  const missingRequired = () => {
    for (const question of orderedQuestions) {
      if (!question.is_required) continue;
      const current = answers[question.id] || {};
      if (question.question_type === 'text') {
        if (!String(current.text_answer || '').trim()) return question;
      } else if (!(current.option_ids || []).length) {
        return question;
      }
    }
    return null;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const missing = missingRequired();
    if (missing) {
      setError(`Ответьте на обязательный вопрос: ${missing.text}`);
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      await axios.post(`/api/webinars/share/${slug}/quiz/`, {
        access: accessToken,
        answers: orderedQuestions.map((question) => ({
          question_id: question.id,
          option_ids: answers[question.id]?.option_ids || [],
          text_answer: answers[question.id]?.text_answer || '',
        })),
      });
      toast.success('Ответы отправлены');
      if (onSubmitted) onSubmitted();
    } catch (err) {
      setError(err.response?.data?.error || 'Не удалось отправить ответы');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="quiz-modal-overlay">
      <div className="quiz-modal-content" role="dialog" aria-modal="true" aria-labelledby="quiz-modal-title">
        <div className="quiz-modal-header">
          <h2 id="quiz-modal-title">Проверка после просмотра</h2>
          <p>Чтобы закрыть это окно, ответьте на все обязательные вопросы.</p>
        </div>
        <form className="quiz-modal-body" onSubmit={handleSubmit}>
          {orderedQuestions.map((question, index) => {
            const current = answers[question.id] || { option_ids: [], text_answer: '' };
            return (
              <div key={question.id} className="quiz-question">
                <div className="quiz-question-title">
                  {index + 1}. {question.text}
                  {question.is_required ? <span className="quiz-required">*</span> : null}
                </div>
                {question.question_type === 'text' ? (
                  <textarea
                    className="form-textarea"
                    rows="3"
                    value={current.text_answer || ''}
                    onChange={(e) => setAnswer(question.id, { text_answer: e.target.value })}
                    placeholder="Ваш ответ"
                    required={question.is_required}
                  />
                ) : (
                  <div className="quiz-options">
                    {(question.options || []).map((option) => {
                      const checked = (current.option_ids || []).includes(option.id);
                      return (
                        <label key={option.id} className={`quiz-option ${checked ? 'is-selected' : ''}`}>
                          <input
                            type={question.question_type === 'multiple' ? 'checkbox' : 'radio'}
                            name={`quiz-q-${question.id}`}
                            checked={checked}
                            onChange={() => toggleOption(question, option.id)}
                          />
                          <span>{option.text}</span>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
          {error ? <div className="quiz-error">{error}</div> : null}
          <button type="submit" className="btn btn-primary quiz-submit" disabled={submitting}>
            {submitting ? 'Отправка...' : 'Отправить ответы'}
          </button>
        </form>
      </div>
    </div>
  );
};

export const QuizNoticeModal = ({ open, onContinue }) => {
  if (!open) return null;
  return (
    <div className="quiz-modal-overlay">
      <div className="quiz-modal-content quiz-notice-content" role="dialog" aria-modal="true">
        <div className="quiz-modal-header">
          <h2>Пройдите опрос после просмотра</h2>
          <p>
            Когда запись закончится, откроется опрос. Окно можно закрыть только после ответов
            на все обязательные вопросы.
          </p>
        </div>
        <div className="quiz-modal-body">
          <button type="button" className="btn btn-primary quiz-submit" onClick={onContinue}>
            Понятно, смотреть
          </button>
        </div>
      </div>
    </div>
  );
};

export default QuizModal;
